import {applyCloneProgress} from './canvas-clone-progress';
import {reconcileRecoveredRunCards} from './recovered-run-cards';
import type { CanvasChatMessage } from './canvas-types';

export interface PlaybookJobView {
  id: number;
  projectId: number;
  assistantMessageId: string;
  kind?: string;
  status: 'queued' | 'running' | 'paused' | 'completed' | 'failed' | 'cancelled';
  position?: number | null;
  activeCount?: number;
  limit?: number;
  cancellationRequested?: boolean;
  error?: string | null;
  workflowRunIds?: number[];
  enqueuedAt?: string;
  result?: unknown;
  lastStatus?: string;
}
export interface PlaybookJobEvent { id: number; event: { type: string; data?: any } }
export const isSettledJob = (job: PlaybookJobView) => ['completed', 'failed', 'cancelled'].includes(job.status);
export const isWaitingForAdmission = (message: CanvasChatMessage) => !!message.playbookJobs?.some(job => job.status === 'queued') && !message.playbookJobs.some(job => job.status === 'running');
const liveStreams = new Set<string>();
export function setPlaybookStreamActive(projectId: number, messageId: string, active: boolean) {
  const key = `${projectId}:${messageId}`;
  if (active) liveStreams.add(key); else liveStreams.delete(key);
}
export const hasLivePlaybookStream = (projectId: number, messageId: string) => liveStreams.has(`${projectId}:${messageId}`);
const owns = (job: PlaybookJobView, projectId: number) => job.projectId === projectId && Number.isSafeInteger(job.id) && job.id > 0 && !!job.assistantMessageId;

/** Job identity, not the currently open chat or last message, owns every update. */
export function mergePlaybookJobs(messages: CanvasChatMessage[], jobs: PlaybookJobView[], projectId: number): CanvasChatMessage[] {
  let next = messages;
  for (const job of jobs.filter(job => owns(job, projectId))) {
    const index = next.findIndex(message => message.id === job.assistantMessageId && message.role === 'assistant');
    const message: CanvasChatMessage = index < 0 ? { id: job.assistantMessageId, role: 'assistant', content: '', isAgentMessage: true, isStreaming: false } : next[index];
    const previous = message.playbookJobs?.find(item => item.id === job.id);
    // An older polling response cannot turn an admitted/completed job back into a queue entry.
    if (previous && ((isSettledJob(previous) && !isSettledJob(job)) || (previous.status === 'running' && job.status === 'queued'))) continue;
    const updated = { ...previous, ...job, lastStatus: job.lastStatus ?? previous?.lastStatus };
    const playbookJobs = [...(message.playbookJobs ?? []).filter(item => item.id !== job.id), updated];
    const replacement = { ...message, playbookJobs, content: message.content.startsWith('_(Previous turn didn\'t complete') ? '' : message.content };
    next = index < 0 ? [...next, replacement] : next.map((item, i) => i === index ? replacement : item);
  }
  return reconcileRecoveredRunCards(next);
}

/** Replay only recorded progress and delivered assets; queued jobs never invent slots. */
export function replayPlaybookJobEvents(messages: CanvasChatMessage[], job: PlaybookJobView, events: PlaybookJobEvent[], projectId: number): CanvasChatMessage[] {
  if (!owns(job, projectId)) return messages;
  return messages.map(message => {
    if (message.id !== job.assistantMessageId || message.role !== 'assistant') return message;
    let next = { ...message, isStreaming: false };
    for (const { event } of events) {
      const data = event.data ?? {};
      if(['step','variation_progress','variation_complete','done','error'].includes(event.type)&&next.agentTaskGroups?.some(group=>group.id.startsWith('clone-'))) {
        next={...next,agentTaskGroups:next.agentTaskGroups.map(group=>group.id.startsWith('clone-')?applyCloneProgress(group,event.type,data):group)};
      }
      if (['canvas_image_ready', 'variation_complete', 'style_complete'].includes(event.type) && typeof data.imageUrl === 'string' && data.imageUrl) {
        const slots = next.imageSlots ?? [];
        if(event.type==='variation_complete'&&Number.isSafeInteger(data.index)&&data.index>=0){
          next={...next,imageSlots:[...slots.filter(slot=>slot.index!==data.index&&slot.resolved!==data.imageUrl),{index:data.index,resolved:data.imageUrl}].sort((a,b)=>a.index-b.index)};
        }else if (!slots.some(slot => slot.resolved === data.imageUrl)) next = { ...next, imageSlots: [...slots, { index: slots.length ? Math.max(...slots.map(slot => slot.index)) + 1 : 0, resolved: data.imageUrl }] };
      }
      if (event.type === 'agent_done' && typeof data.text === 'string' && data.text.trim()) next = { ...next, content: data.text, isStreaming: false };
      const status = event.type === 'playbook_phase_progress' ? data.statusText : event.type === 'playbook_phase_start' ? data.phaseName : event.type === 'agent_thinking' ? data.text : undefined;
      if (typeof status === 'string' && status.trim()) next = { ...next, playbookJobs: next.playbookJobs?.map(item => item.id === job.id ? { ...item, lastStatus: status } : item) };
    }
    if((job.status==='failed'||job.status==='cancelled')&&next.agentTaskGroups){
      next={...next,agentTaskGroups:next.agentTaskGroups.map(group=>group.id.startsWith('clone-')?applyCloneProgress(group,'error',{}):group)};
    }
    return next;
  });
}

export async function pollPlaybookJobsOnce(options: {
  projectId: number; cursors: Map<number, number>; settledJobs?: Set<number>;
  request?: typeof fetch;
  getMessages: () => CanvasChatMessage[];
  writeMessages: (update: (messages: CanvasChatMessage[]) => CanvasChatMessage[]) => void;
  reconcile: () => Promise<void>;
  isCurrent: () => boolean;
}): Promise<void> {
  if (!options.isCurrent()) return;
  const request = options.request ?? fetch;
  const response = await request(`/api/playbook-jobs?projectId=${options.projectId}`);
  if (!response.ok || !options.isCurrent()) return;
  const payload = await response.json();
  const jobs: PlaybookJobView[] = Array.isArray(payload.jobs) ? payload.jobs.filter((job: PlaybookJobView) => owns(job, options.projectId)) : [];
  if (!options.isCurrent()) return;
  options.writeMessages(messages => mergePlaybookJobs(messages, jobs, options.projectId));
  let reconcile = false;
  for (const job of jobs) {
    if (!options.isCurrent()) return;
    if (hasLivePlaybookStream(options.projectId, job.assistantMessageId) || options.settledJobs?.has(job.id) || job.status === 'queued') continue;
    const eventsResponse = await request(`/api/playbook-jobs/${job.id}/events?after=${options.cursors.get(job.id) ?? 0}`);
    if (!eventsResponse.ok || !options.isCurrent()) continue;
    const replay = await eventsResponse.json();
    if (!options.isCurrent()) return;
    const events: PlaybookJobEvent[] = Array.isArray(replay.events) ? replay.events : [];
    const latest: PlaybookJobView = replay.job && owns(replay.job, options.projectId) ? replay.job : job;
    options.writeMessages(messages => replayPlaybookJobEvents(mergePlaybookJobs(messages, [latest], options.projectId), latest, events, options.projectId));
    if (Number.isSafeInteger(replay.nextCursor)) options.cursors.set(job.id, replay.nextCursor);
    if (events.some(({ event }) => ['canvas_image_ready', 'canvas_image_pending', 'variation_complete', 'style_complete', 'playbook_complete'].includes(event.type))) reconcile = true;
    if (isSettledJob(latest) && events.length < 500) {
      reconcile = true; // A failed planner may settle after preview with no final image event.
      options.settledJobs?.add(job.id);
    }
  }
  if (reconcile && options.isCurrent()) await options.reconcile();
}
